import React, { useId } from 'react';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import useClassNames from 'helpers/hooks/useClassNames';
import { useQuantityInput } from './hooks/useQuantityInput';
import { QuantitySelectorProps } from './types';

const QuantitySelector = ({
  id: idProp,
  value,
  defaultValue = 0,
  minValue = 0,
  maxValue = Infinity,
  disabled = false,
  className: classNameProp,
  onChange,
}: QuantitySelectorProps) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;

  const { increment, decrement, canIncrement, canDecrement, inputProps } = useQuantityInput({
    value,
    defaultValue,
    minValue,
    maxValue,
    disabled,
    onChange,
  });

  const className = useClassNames([
    'flex w-fit items-center rounded-sm border-2 border-stroke-primary transition',
    !disabled ? 'hover:border-stroke-secondary' : '',
    disabled ? 'cursor-not-allowed' : 'bg-white text-primary',
    classNameProp,
  ]);

  // Prevent blur when clicking buttons (avoids race condition between blur and click)
  const preventBlur = (e: React.MouseEvent) => e.preventDefault();

  return (
    <div className={className}>
      <button
        type="button"
        aria-label="Decrease quantity"
        disabled={!canDecrement}
        onMouseDown={preventBlur}
        onClick={decrement}
        className="flex min-h-44 min-w-44 cursor-pointer items-center justify-center self-stretch border-r-2 border-stroke-primary p-6 disabled:cursor-not-allowed disabled:text-feedback-disabled md:p-10"
      >
        <MinusIcon className="size-16 md:size-20" strokeWidth={1.5} />
      </button>
      {/* type="text" with inputMode="numeric" provides better UX than type="number":
          - Shows numeric keyboard on mobile
          - Avoids browser-native spinners and scroll-to-change behavior
          - Allows full control over validation and formatting */}
      <input
        id={id}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label="Quantity"
        className="body-text-small w-40 border-0 bg-transparent text-center !text-16 font-medium outline-none focus:ring-0 disabled:cursor-not-allowed md:w-56 md:!text-14"
        {...inputProps}
      />
      <button
        type="button"
        aria-label="Increase quantity"
        disabled={!canIncrement}
        onMouseDown={preventBlur}
        onClick={increment}
        className="flex min-h-44 min-w-44 cursor-pointer items-center justify-center self-stretch border-l-2 border-stroke-primary p-6 disabled:cursor-not-allowed disabled:text-feedback-disabled md:p-10"
      >
        <PlusIcon className="size-16 fill-icon-primary md:size-20" strokeWidth={1.5} />
      </button>
    </div>
  );
};

export default QuantitySelector;
